import { Injectable } from '@angular/core';
import { AngularMode, Project, ProjectInfo, ProjectType } from '@frontend-site/shared/data';
import { Repository } from '@frontend-site/shared/utils';
import { createStore, select, withProps } from '@ngneat/elf';
import { filter, Observable } from 'rxjs';
import { ProjectState } from './project-state';

function createInitialState(): ProjectState {
    return {
        projects: [],
        projectDetails: null
    };
}

@Injectable({ providedIn: 'root' })
export class ProjectStore extends Repository<ProjectState> {
    projects$: Observable<Project[]> = this.store.pipe(select((state) => state.projects));
    activeProjects$: Observable<Project[]> = this.projects$.pipe(select((state) => state.filter((p) => (p.projectStatus = 'ACTIVE'))));
    projectDetails$: Observable<ProjectInfo | null> = this.store.pipe(select((state) => state.projectDetails));

    projectId$: Observable<number> = this.store.pipe(
        filter((state) => !!state.selectedProjectId),
        select((m) => m.selectedProjectId as number)
    );

    get projectDetails(): ProjectInfo | null {
        return this.getValue().projectDetails;
    }

    get projectId(): number | null | undefined {
        return this.getValue().selectedProjectId;
    }

    constructor() {
        super(createStore({ name: 'ProjectStore' }, withProps<ProjectState>(createInitialState())));
    }

    setProjects(projects: Project[]): void {
        this.update({ projects });
    }

    updateSelectedProjectDetails(projectDetails: ProjectInfo): void {
        this.store.update((state) => {
            const projects = state.projects.map((project) => {
                if (project.projectId === projectDetails.id) {
                    return { ...project, lastLoginDate: Date.now() };
                }
                return project;
            });
            return { ...state, projectDetails, projects, selectedProjectId: projectDetails.id };
        });
    }

    clearSelectedProject(): void {
        this.update({ selectedProjectId: null, projectDetails: null });
    }

    public get isInternal(): boolean {
        return this.projectDetails?.angularMode === AngularMode.INTERNAL;
    }

    public get isAlpha(): boolean {
        return this.projectDetails?.angularMode === AngularMode.ALPHA;
    }

    public get isBeta(): boolean {
        return this.projectDetails?.angularMode === AngularMode.BETA;
    }

    public get isGA(): boolean {
        return this.projectDetails?.angularMode === AngularMode.GA;
    }

    public getSideBySideOtherProject(): Project | null {
        const projects: Project[] = this.getValue().projects || [];
        const sideBySideProject = projects.find(
            (project) =>
                project.projectType == ProjectType.ICR_3 &&
                project.systemId === this.projectDetails?.systemId &&
                project.projectId !== this.projectDetails?.projectId
        );
        return sideBySideProject || null;
    }
}
